import { ensureNoUndefinedProperties } from '@/Shared/Helper/EnsureNoUndefinedProperties'
import CompanyID from '@/App/Company/Entity/CompanyID'
import {
    DayOfWeekEnum,
    type AvailabilityData,
    type DateSpecificHoursType,
    type WeeklyHoursType,
} from '@/Shared/AutoGenerated/Main'
import AvailabilityID from '@/App/Availability/Entity/AvailabilityID'
import CompanyUser from '@/App/CompanyUser/Entity/CompanyUser'
import CompanyUserID from '@/App/CompanyUser/Entity/CompanyUserID'
import useId from '@/Shared/Hook/useId'
import useDate from '@/Shared/Hook/useDate'

export default class Availability {
    constructor(
        public readonly id: AvailabilityID,
        public readonly companyId: CompanyID,
        public readonly createdBy: CompanyUserID,
        public readonly name: string,
        public readonly weeklyHours: WeeklyHoursType[],
        public readonly dateSpecificHours: DateSpecificHoursType[],
        public readonly timeZone: string,
    ) {
        ensureNoUndefinedProperties(this)
    }

    canBeManagedBy(companyUser: CompanyUser): boolean {
        return (
            this.createdBy.value === companyUser.id.value ||
            companyUser.isAdminOrOwner()
        )
    }

    cannotBeManagedBy(companyUser: CompanyUser): boolean {
        return !this.canBeManagedBy(companyUser)
    }

    static newWeeklyHours(dayOfWeek: DayOfWeekEnum): WeeklyHoursType {
        return {
            id: useId().make(),
            dayOfWeek: dayOfWeek,
            timeRanges: [],
        }
    }

    static newDateSpecificHours(): DateSpecificHoursType {
        return {
            id: useId().make(),
            startDate: useDate().getCurrentDate(),
            endDate: useDate().getCurrentDate(),
            timeRanges: [],
        }
    }

    // ------------------------
    // Hydration
    // ------------------------

    static hydrateSingleOrNull(
        entityData: AvailabilityData | null,
    ): Availability | null {
        if (!entityData) {
            return null
        }

        return this.hydrateSingle(entityData)
    }

    static hydrateSingle(entityData: AvailabilityData): Availability {
        return new Availability(
            new AvailabilityID(entityData.id),
            new CompanyID(entityData.companyId),
            new CompanyUserID(entityData.createdById),
            entityData.name,
            entityData.weeklyHours,
            entityData.dateSpecificHours,
            entityData.timeZone,
        )
    }

    static hydrateMultiple(entitiesData: AvailabilityData[]): Availability[] {
        return entitiesData.map((entityData) => this.hydrateSingle(entityData))
    }
}
